// Scratchpad state projection: an agent re-reads a JSON artifact it collected
// earlier in the session and prints an ad-hoc projection of it.
//
// The first lens the factory selected on its own. The loop mined the largest
// staging tenant with no predicate, and a judge reading only the built payload
// named this out of ten candidates — 42 instances across 5 sessions and 4
// endpoints, the shape being `python -c` / a heredoc over a JSON file the agent
// itself wrote.
//
// Why it is worth a lens, in the judge's words: it tells you whether an agent's
// later conclusions rest on state it actually re-checked or on a stale file it
// never re-read. And it is invisible to every name-level extractor, because the
// tool call is `Bash` and the program is a string argument.
//
// WHAT THE CORPUS ACTUALLY LOOKS LIKE:
//   python -c "import json;d=json.load(open('state.json'));print(len(d['issues']))"
//   python - <<'EOF' … st=json.load(open(S+"/pr657-statuses.json")) … EOF
//   python3 -c " import json; d=json.load(open('.tmp_pr547/issue_comments.json'))
//                for c in d: print('=====', c['id'], c['user']['login']) "
//
// Three things shape the grammar, all read off real instances:
//
//   1. THE PATH IS USUALLY BUILT, not literal — `S+"/pr657-statuses.json"`,
//      `d+'/'+fn`, `d+'/meta.json'`. A literal-only match finds a third of them,
//      so the basename is recovered from any string fragment ending in `.json`.
//   2. THE PROJECTION IS THE POINT. Which keys were read — `.get("status")`,
//      `['user']['login']` — is what the agent was actually asking, and it is the
//      one thing a reader cannot get from the file itself.
//   3. WHAT IT DID WITH THEM DIFFERS: count, list, or reduce. A `len(...)` is a
//      check; a `for … print` is an inspection; a dict accumulator is a dedup or a
//      latest-wins fold. Reporting all three as "read a file" loses the question.

import { embeddedSource } from "../../src/mine/skeleton.js";
import {
  resolveShellVars, splitStatements, tokenize, stripAnsi,
  HARNESS_NOTICE_RE, SHELL_ERROR_RE, SHELL_TOOL_NAMES,
} from "../../src/mine/shell.js";

const OUT_CAP = 8000;
const PY_RUNNER_RE = /^(?:python3?(?:\.\d+)?|py|uv|poetry|pdm|rye|pipenv)$/i;

// `json.load(open(...))` / `json.loads(Path(...).read_text())`. The argument is
// captured loosely because it is usually an expression, not a literal.
const LOAD_RE = /json\.loads?\s*\(\s*([^)]{0,200})/g;
// Any string fragment naming a .json file, wherever it sits in that expression.
const JSON_NAME_RE = /['"]([^'"]*?[\w.\-]+\.json)['"]/g;

// A key read off the loaded object. Both spellings appear, often in one program.
const GET_RE = /\.get\(\s*['"]([\w.\-]{1,48})['"]/g;
const SUBSCRIPT_RE = /\[\s*['"]([\w.\-]{1,48})['"]\s*\]/g;

// What the program DID with the projection.
const SHAPES = [
  ["count", /\blen\s*\(/],
  ["reduce", /^\s*\w+\s*=\s*\{\s*\}\s*$/m],
  ["list", /\bprint\s*\(/],
];

// A conditional that narrows the rows — the agent's own filter, and a statement
// about what it was looking for.
const FILTER_RE = /^\s*if\s+(.{3,120}?)\s*:/gm;

const str = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  return "";
}

function uniq(re, text, cap = 12) {
  const out = [];
  re.lastIndex = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m[1] && !out.includes(m[1])) out.push(m[1]);
    if (out.length >= cap) break;
  }
  return out;
}

/** The JSON artifacts a program loaded, by basename. */
export function artifactsOf(source) {
  const src = String(source || "");
  const out = [];
  LOAD_RE.lastIndex = 0;
  for (let m = LOAD_RE.exec(src); m; m = LOAD_RE.exec(src)) {
    // The path is an expression more often than a literal, so every .json-looking
    // string fragment inside it counts.
    for (const name of uniq(JSON_NAME_RE, m[1], 4)) {
      const base = name.split(/[\\/]/).pop();
      if (base && !out.includes(base)) out.push(base);
    }
  }
  // A loop over directories with a constant filename reads the name outside the
  // load expression, so fall back to the whole program.
  if (!out.length) {
    for (const name of uniq(JSON_NAME_RE, src, 4)) {
      const base = name.split(/[\\/]/).pop();
      if (base && !out.includes(base)) out.push(base);
    }
  }
  return out;
}

/** Which keys the program read — the projection, and the actual question asked. */
export function projectionOf(source) {
  const src = String(source || "");
  const keys = [...uniq(GET_RE, src, 16), ...uniq(SUBSCRIPT_RE, src, 16)];
  return [...new Set(keys)].slice(0, 16);
}

export function shapeOf(source) {
  const src = String(source || "");
  for (const [name, re] of SHAPES) if (re.test(src)) return name;
  return "other";
}

export function filtersOf(source) {
  return uniq(FILTER_RE, String(source || ""), 6).map((f) => f.slice(0, 100));
}

/** One prompt's turns → its state projections. */
export function extractProjectionOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }

  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      if (!SHELL_TOOL_NAMES.test(String(b.name || "").split("__").pop())) continue;
      const cmd = str(b.input?.command) || str(b.input?.cmd) || str(b.input?.script);
      if (!cmd) continue;
      const text = resolveShellVars(cmd);

      // The Python door: a statement whose program is an interpreter, carrying an
      // inline program. Anything else on the line is positioning.
      let source = null;
      for (const st of splitStatements(text)) {
        const toks = tokenize(st.text.trim());
        let i = 0;
        while (i < toks.length && (/^\$?\w+=/.test(toks[i]) || /^&&?$/.test(toks[i]) || /^(?:export|set|cd|time)$/i.test(toks[i]))) i += 1;
        const prog = (toks[i] || "").split(/[\\/]/).pop().replace(/\.exe$/i, "");
        if (!PY_RUNNER_RE.test(prog)) continue;
        const s = embeddedSource(st.text) || embeddedSource(text);
        if (s && /json\.loads?\s*\(/.test(s)) { source = s; break; }
      }
      // A program that never loads JSON is not this behaviour. The door is the
      // LOAD, not the interpreter — `python -c "print(1)"` is not a projection.
      if (!source) continue;

      const artifacts = artifactsOf(source);
      if (!artifacts.length) continue;

      // Did this call WRITE the artifact it then queried? The audit surfaced this:
      // `tea api … > /tmp/revs.json && python -c "…json.load(open('/tmp/revs.json'))"`
      // collects and queries in one breath. That is a different act from re-reading
      // state from earlier in the session — one is "fetch and look", the other is
      // "do I still believe what I recorded". Conflating them is what the lens
      // exists to avoid, since the whole point is whether a conclusion rests on
      // re-checked state.
      const wroteHere = artifacts.some((a) => new RegExp(`>\\s*\\S*${a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(text));

      const result = b.id ? resultById.get(b.id) : null;
      const errored = Boolean(result?.isError) || (result ? contentText(result.content).includes("<tool_use_error>") : false);
      const rawOut = !errored && result ? contentText(result.content) : "";
      const outputStatus = !rawOut ? "none"
        : HARNESS_NOTICE_RE.test(rawOut) ? "backgrounded"
        : SHELL_ERROR_RE.test(rawOut) ? "shell-error"
        : "observed";
      const out = outputStatus === "observed" ? stripAnsi(rawOut) : "";
      // A traceback means the projection failed — the agent asked and got nothing,
      // which is a different fact from asking and getting an empty answer.
      const raised = /Traceback \(most recent call last\)|\b(?:KeyError|JSONDecodeError|FileNotFoundError|TypeError)\b/.test(out);

      ops.push({
        key: b.id || `synth:proj:${artifacts.join(",")}`,
        artifacts,
        projection: projectionOf(source),
        shape: shapeOf(source),
        filters: filtersOf(source),
        program: source.length > 1200 ? `${source.slice(0, 1200)}…` : source,
        rows: out ? out.split("\n").filter((l) => l.trim()).length : 0,
        // "collected here" vs "recalled from earlier" — the distinction the whole
        // lens turns on.
        origin: wroteHere ? "same-call" : "earlier",
        raised,
        output: out ? (out.length > OUT_CAP ? out.slice(0, OUT_CAP) : out) : null,
        outputStatus,
        ok: !errored,
      });
    }
  }
  return ops;
}

/** Why this op shows no answer. Only one of these states is the program's own. */
export function describeMissing(op) {
  if (op.raised) return "the program raised — the projection asked for something the artifact does not have";
  if (op.outputStatus === "observed" && op.rows === 0) return "the projection ran and matched nothing in the artifact";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — its output never entered this trace";
    case "shell-error": return "the captured text was the shell's error, not the program's output";
    case "none": return "ran and captured no output";
    default: return null;
  }
}
